'use client';

import { motion } from 'framer-motion';
import { HiHome } from 'react-icons/hi';
import Link from 'next/link';
import Image from 'next/image';

export default function NotFound() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const floatVariants = {
    initial: { y: 0 },
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const pulseVariants = {
    initial: { scale: 1 },
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const rotateVariants = {
    initial: { rotate: 0 },
    rotate: {
      rotate: 360,
      transition: {
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  const shimmerVariants = {
    initial: { backgroundPosition: '-200% 0' },
    shimmer: {
      backgroundPosition: ['0% 0%', '200% 0%'],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen bg-[var(--color-dark-navy)] flex flex-col items-center justify-center p-4 overflow-hidden"
    >
      <motion.header
        variants={itemVariants}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="absolute top-8 left-8 z-10"
      >
        <Link href="/">
          <motion.div
            whileHover={{ rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Image
              src="/imgs/logo-bg.png"
              alt="Logo"
              width={120}
              height={60}
              className="hover:opacity-80 transition-opacity"
              priority
            />
          </motion.div>
        </Link>
      </motion.header>

      <motion.div 
        variants={containerVariants}
        className="text-center max-w-2xl mx-auto z-10"
      >
        <motion.div
          variants={itemVariants}
          animate="float"
          variants={floatVariants}
          className="mb-8"
        >
          <motion.div
            whileHover="pulse"
            variants={pulseVariants}
          >
            <Image
              src="/imgs/404.svg"
              alt="404 Error"
              width={400}
              height={300}
              className="mx-auto w-full max-w-md"
              priority
            />
          </motion.div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="mb-8"
        >
          <motion.h1 
            className="text-6xl md:text-7xl font-bold text-[var(--color-gold-bright)] mb-4"
            animate="pulse"
            variants={pulseVariants}
          >
            Oops!
          </motion.h1>
          
          <motion.h2 
            className="text-2xl md:text-3xl font-semibold text-[var(--color-white)] mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Page Not Found
          </motion.h2>
          
          <motion.p 
            className="text-lg text-[var(--color-white)]/80 mb-8 max-w-md mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            The page you're looking for seems to have wandered off into the digital 
            wilderness. Let's get you back to familiar territory.
          </motion.p>
        </motion.div>

        <motion.div
          variants={itemVariants}
        >
          <motion.div
            animate="shimmer"
            variants={shimmerVariants}
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              backgroundSize: '200% 100%'
            }}
            className="rounded-lg inline-block"
          >
            <Link
              href="/"
              className="inline-flex items-center gap-2 bg-[var(--color-gold-bright)] text-[var(--dark-navy)] px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg relative overflow-hidden"
            >
              <motion.span
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <HiHome className="text-xl" />
              </motion.span>
              <motion.span
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                Back to Homepage
              </motion.span>
              
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                whileHover={{ opacity: 0.3, x: '100%' }}
                transition={{ duration: 0.6 }}
              />
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate="float"
          variants={floatVariants}
          transition={{ duration: 6, repeat: Infinity, delay: 0.2 }}
          className="absolute -top-24 -right-24 w-64 h-64 border-4 border-[var(--color-gold-bright)]/10 rounded-full"
        />
        
        <motion.div
          animate="float"
          variants={floatVariants}
          transition={{ duration: 8, repeat: Infinity, delay: 0.5 }}
          className="absolute -bottom-24 -left-24 w-64 h-64 border-4 border-[var(--color-olive-dark)]/10 rounded-full"
        />
        
        <motion.div
          animate="rotate"
          variants={rotateVariants}
          className="absolute top-1/4 right-1/4 w-32 h-32 border-2 border-[var(--color-gold-bright)]/20 rounded-full"
        />
        
        <motion.div
          animate="rotate"
          variants={rotateVariants}
          transition={{ duration: 25, repeat: Infinity, reverse: true }}
          className="absolute bottom-1/4 left-1/4 w-40 h-40 border-2 border-[var(--color-olive-dark)]/20 rounded-full"
        />

        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[var(--color-gold-bright)]/30 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + i * 10}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          />
        ))}

        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[var(--color-olive-dark)]/30 rounded-full"
            style={{
              right: `${20 + i * 15}%`,
              bottom: `${10 + i * 10}%`,
            }}
            animate={{
              y: [0, 30, 0],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.7,
            }}
          />
        ))}
      </div>

      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            'radial-gradient(circle at 30% 30%, rgba(120, 119, 116, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 70% 70%, rgba(120, 119, 116, 0.15) 0%, transparent 50%)',
            'radial-gradient(circle at 30% 30%, rgba(120, 119, 116, 0.1) 0%, transparent 50%)',
          ],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.div>
  );
}